import { PDFAnnotator, type Selection } from "./components/PDFAnnotator";
import { Page } from "@/app/shared/components/page";
import { PageHeader } from "@groot/shell/components/layout";
import { Button } from "@groot/ui/button";

import { LoadingSpinner } from "@groot/ui/loading-spinner";
import { toast } from "@/lib/toast";
import { apiClient } from "@/services/api";
import { ArrowLeft, Send, Upload } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { Statement as StatementType } from "@/types";

type Statement = StatementType;

export function StatementAnnotate() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [statement, setStatement] = useState<Statement | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [selections, setSelections] = useState<Selection[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [quickExtracting, setQuickExtracting] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: loadStatement is not stable
  useEffect(() => {
    loadStatement();
  }, [id]);

  const loadStatement = async () => {
    try {
      setLoading(true);
      const data = await apiClient.getStatement(Number(id));
      setStatement(data);

      const proxyUrl = `/api/v1/storage/proxy?filePath=${encodeURIComponent(data.fileS3Path)}`;
      setPdfUrl(proxyUrl);
    } catch {
      toast({
        title: "Error",
        description: "Failed to load statement",
        variant: "destructive",
      });
      navigate("/statements");
    } finally {
      setLoading(false);
    }
  };

  const initialSelections: Selection[] = useMemo(() => {
    if (!statement || !Array.isArray(statement.selectionData)) return [];
    return (
      statement.selectionData as Array<{
        pageNumber: number;
        x: number;
        y: number;
        width: number;
        height: number;
        imageData?: string;
      }>
    ).map((s, idx) => ({
      id: `${s.pageNumber}-${s.x}-${s.y}-${s.width}-${s.height}-${idx}`,
      pageNumber: s.pageNumber,
      x: s.x,
      y: s.y,
      width: s.width,
      height: s.height,
      imageData: s.imageData,
    }));
  }, [statement]);

  const handleSubmit = async () => {
    if (selections.length === 0) {
      toast({
        title: "No selections",
        description: "Please select at least one area to extract",
        variant: "destructive",
      });
      return;
    }

    try {
      setSubmitting(true);

      // Submit selections to backend
      await apiClient.submitStatementSelections(Number(id), {
        selections: selections.map((s) => ({
          pageNumber: s.pageNumber,
          x: s.x,
          y: s.y,
          width: s.width,
          height: s.height,
          imageData: s.imageData,
        })),
      });

      toast({
        title: "Processing",
        description: "Extracting transactions from selected areas...",
      });

      navigate(`/statements/${id}`);
    } catch {
      toast({
        title: "Error",
        description: "Failed to submit selections",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleQuickExtract = async () => {
    try {
      setQuickExtracting(true);
      await apiClient.extractStatement(Number(id));
      toast({
        title: "Queued",
        description: "Full PDF extraction queued",
      });
      navigate(`/statements/${id}`);
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to queue extraction",
        variant: "destructive",
      });
    } finally {
      setQuickExtracting(false);
    }
  };

  if (loading) {
    return (
      <Page>
        <LoadingSpinner size="lg" className="py-20" />
      </Page>
    );
  }

  if (!statement || !pdfUrl) {
    return (
      <Page>
        <p className="text-muted-foreground">Statement not found</p>
      </Page>
    );
  }

  return (
    <Page>
      {/* Header */}
      <div className="mb-8">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/statements")}
          className="mb-4 -ml-2"
        >
          <ArrowLeft className="size-4" />
        </Button>
        <PageHeader
          title="Select Transaction Areas"
          description={`Annotate: ${statement.fileName}`}
          actions={
            <div className="flex flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={() => navigate("/statements")}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={selections.length === 0 || submitting}>
                {submitting ? (
                  <>
                    <LoadingSpinner className="size-4" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Send className="size-4" />
                    Extract Transactions ({selections.length} area
                    {selections.length !== 1 ? "s" : ""})
                  </>
                )}
              </Button>
              <Button variant="outline" onClick={handleQuickExtract} disabled={quickExtracting}>
                {quickExtracting ? (
                  <LoadingSpinner className="size-4" />
                ) : (
                  <Upload className="size-4" />
                )}
                Extract whole PDF
              </Button>
            </div>
          }
        />
      </div>

      <div className="space-y-6">
        {/* Instructions */}
        <div className="border border-border rounded-lg p-6">
          <h3 className="font-medium text-foreground mb-3">Instructions:</h3>
          <ol className="text-sm text-muted-foreground list-decimal list-inside space-y-1">
            <li>Click and drag on the PDF to select transaction table areas</li>
            <li>You can make multiple selections across different pages</li>
            <li>Each selected area will be sent to AI for extraction</li>
            <li>Click "Extract Transactions" when ready</li>
          </ol>
        </div>

        {/* PDF Annotator */}
        <PDFAnnotator
          pdfUrl={pdfUrl}
          onSelectionsChange={setSelections}
          initialSelections={initialSelections}
        />

        {/* Actions moved to header */}
      </div>
    </Page>
  );
}
